import { BehaviorSubject, skip, Subscription } from 'rxjs'

const drawerTogglingAnimationDuration = 500
const focusDelays = [10, 100, 500]
const phaseToolbarWidthOffset = 120

export interface DrawerModelService {
  leftDrawerVisible$: BehaviorSubject<boolean>
  rightDrawerVisible$: BehaviorSubject<boolean>
  hasRightDrawerContent: boolean
}

export interface ElementSize {
  width: number
  height: number
}

export class AutoResizeElementService {
  private readonly _leftDrawerVisibleSubscription: Subscription
  private readonly _rightDrawerVisibleSubscription: Subscription | undefined
  private _autoResize = false
  private _desiredWidth = 0
  private _desiredHeight = 0
  private _minWidth = 10
  private _htmlElement: HTMLElement | undefined
  private _lastWidth = 0
  private _lastHeight = 0
  private _widthOffset = 0
  private _resizeObserver: ResizeObserver | undefined

  constructor(drawerModelService: DrawerModelService, private readonly setNewSize: (size: ElementSize) => void) {
    const drawerToggled = () => {
      // console.log('drawerToggled')
      this.followDrawerToggling(Date.now() + drawerTogglingAnimationDuration)
    }
    this._leftDrawerVisibleSubscription = drawerModelService.leftDrawerVisible$
      .pipe(skip(1))
      .subscribe((_loaded) => drawerToggled())
    if (drawerModelService.hasRightDrawerContent) {
      this._rightDrawerVisibleSubscription = drawerModelService.rightDrawerVisible$
        .pipe(skip(1))
        .subscribe((_loaded) => drawerToggled())
    }
    window.addEventListener('focus', () => {
      // console.log('focus')
      if (this.shouldCalculateSize()) {
        focusDelays.forEach((delay) => {
          setTimeout(() => {
            this.calculateComponentSizeForAvailableWidth()
          }, delay)
        })
      }
    })
  }

  setReferenceId(id: string): void {
    this._htmlElement = document.getElementById(id) as HTMLElement
    // console.log('this._htmlElement', this._htmlElement)
    this._resizeObserver = new ResizeObserver((_entries) => {
      // console.log('resize', entries, entries[0].contentRect.width)
      this.calculateComponentSizeForAvailableWidth()
    })
    this._resizeObserver.observe(this._htmlElement)

    this.calculateComponentSizeForAvailableWidth()
  }

  destroy(): void {
    // console.log('destroy')
    this._leftDrawerVisibleSubscription?.unsubscribe()
    this._rightDrawerVisibleSubscription?.unsubscribe()
    this._resizeObserver?.disconnect()
  }

  setDesiredSize(width: number, height: number): void {
    this._desiredWidth = width
    this._desiredHeight = height
    this.calculateComponentSizeForAvailableWidth()
  }

  setMinWidth(maxWidth: number): void {
    this._minWidth = maxWidth
    this.calculateComponentSizeForAvailableWidth()
  }

  usePhaseToolbar() {
    this._widthOffset = phaseToolbarWidthOffset
    this.setReferenceId('phase-toolbar')
  }

  setAutoResize(autoResize: boolean): void {
    this._autoResize = autoResize
    this.calculateComponentSizeForAvailableWidth()
  }

  getAutoResize(): boolean {
    return this._autoResize
  }

  private followDrawerToggling(endTime: number) {
    const updateFrame = () => {
      if (Date.now() > endTime) {
        return
      }
      this.calculateComponentSizeForAvailableWidth()
      requestAnimationFrame(updateFrame)
    }
    requestAnimationFrame(updateFrame)
  }

  private calculateComponentSizeForAvailableWidth(): void {
    if (!this.shouldCalculateSize()) {
      return
    }
    const displayedImageWidth = this._htmlElement!.clientWidth - this._widthOffset
    if (displayedImageWidth <= 0) {
      return
    }
    let newWidth = this._desiredWidth
    let newHeight = this._desiredHeight
    if (displayedImageWidth < this._desiredWidth) {
      newWidth = Math.max(displayedImageWidth, this._minWidth)
      newHeight = (newWidth / this._desiredWidth) * this._desiredHeight
    }
    if (this._lastWidth !== newWidth || this._lastHeight !== newHeight) {
      this._lastWidth = newWidth
      this._lastHeight = newHeight
      this.setNewSize({ width: newWidth, height: newHeight })
    }
    // console.log('adjusted size', this._lastWidth, this._lastHeight)
  }

  private shouldCalculateSize(): boolean {
    return this._autoResize && !!this._htmlElement && this._desiredWidth > 0 && this._desiredHeight > 0
  }
}
